<template>
  <div class="bid-for-charge">
    <div v-if="showAgentInfo" class="card">
      <div class="section">
        <base-section-title class="section-title" title="项目信息" />
        <div class="section-content">
          <jczh-descriptions
            :info="agencyInfo"
            :option="agencyInfoOption"
            :el-attrs="{
              labelStyle: {
                whiteSpace: 'nowrap'
              }
            }"
          >
            <template v-slot:projectName="{ value }">
              <span v-if="readOnly">{{ value }}</span>
              <template v-else>
                <span v-if="value !== '-'" class="m-r-8">{{ value }}</span>
                <a-button
                  type="link"
                  style="height: 0"
                  @click="projectDialogVisible = true"
                >
                  {{ agencyInfo.projectId ? '更换项目' : '选择项目' }}
                </a-button>
              </template>
            </template>
            <template v-slot:purchaseLink>
              <span class="m-r-8">{{ agencyInfo.purchaseLink || '-' }}</span>
              <span>{{ agencyInfo.linkTelephone || '-' }}</span>
              <a-button
                type="link"
                style="height: 0"
                @click="agencyInfoDialogVisible = true"
              >
                查看更多
              </a-button>
            </template>
          </jczh-descriptions>
        </div>
      </div>
    </div>
    <div v-if="(!readOnly && bidInfo.orderId) || readOnly" class="card">
      <div class="section">
        <base-section-title class="section-title" title="项目信息" />
        <div class="section-content">
          <jczh-descriptions
            :info="bidInfo"
            :option="bidInfoOption"
            :el-attrs="{
              labelStyle: {
                whiteSpace: 'nowrap'
              },
              contentStyle: {
                display: 'inline'
              }
            }"
          >
            <template v-slot:budget="{ value }">
              <div>{{ value | moneyFilter }} 万元</div>
              <money-in-cn :money="value * 10000" />
            </template>
          </jczh-descriptions>
        </div>
      </div>
    </div>

    <!-- 选择已完成的招标项目 -->
    <select-bid-for-charge
      :visible.sync="projectDialogVisible"
      @select="handleBidSelect"
    />

    <!-- 委托项目信息弹窗 -->
    <a-modal
      v-model="agencyInfoDialogVisible"
      title="项目信息"
      :mask-closable="false"
      :footer="null"
    >
      <jczh-descriptions
        :info="agencyInfo"
        :option="agencyInfoDialogOption"
        :el-attrs="{
          column: 1,
          labelStyle: {
            whiteSpace: 'nowrap'
          },
          contentStyle: {
            display: 'inline'
          }
        }"
      >
        <template v-slot:projectBudget="{ value }">
          <div>{{ value | moneyFilter }}万元</div>
          <money-in-cn :money="value * 10000" />
        </template>
        <template v-slot:attachmentGroupId="{ value }">
          <upload
            v-if="value !== '-'"
            :props-data="{
              groupIdBefore: value,
              disabled: true
            }"
          />
          <span v-else>-</span>
        </template>
      </jczh-descriptions>
    </a-modal>
  </div>
</template>

<script>
// 组件
import BaseSectionTitle from '@/components/common/BaseSectionTitle.vue'
import MoneyInCn from '@/components/common/MoneyInCn.vue'
import Upload from '@/components/common/Upload/index.vue'
import SelectBidForCharge from '@/components/entrust/agency-service-fee/SelectBidForCharge.vue'
// 字典
import { projectTypeDict, releaseMediaDict, offerTypeDict } from '@/dictionary/project/common'
import { bidWinWayDict } from '@/dictionary/project/bid'
// 接口
import { getEntrustInfoByOrderIdApi } from '@/api/procure/entrust'
import {
  fetchInviteBidDetailApi,
  getWinBidLatestApi
} from '@/api/procure/invite-bid'
import { strToThousands } from '@/utils/utils'

/**
 * @description 服务费收取关联项目信息
 */
export default {
  name: 'BidForCharge',

  components: { BaseSectionTitle, MoneyInCn, Upload, SelectBidForCharge },

  filters: {
    moneyFilter(value) {
      return strToThousands(value)
    }
  },

  props: {
    readOnly: {
      type: Boolean,
      default: false
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    // 是否显示委托信息
    showAgentInfo: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      // 委托项目信息
      agencyInfo: {},
      agencyInfoOption: [],
      agencyInfoDialogVisible: false,
      agencyInfoDialogOption: [
        { label: '项目名称', prop: 'projectName' },
        { label: '项目编号', prop: 'projectCode' },
        { label: '项目概算', prop: 'projectBudget', slotName: 'projectBudget' },
        {
          label: '项目类型',
          prop: 'projectType',
          filter: value => projectTypeDict.translate(value)?.label || '-'
        },
        {
          label: '项目地点',
          prop: '',
          filter: value =>
            value?.country === '中国'
              ? `${value?.province} - ${value?.city} - ${value.area}`
              : '境外'
        },
        {
          label: '详细地址',
          prop: '',
          filter: value =>
            value?.country === '中国'
              ? value?.address || '-'
              : value?.foreignAddress || '-'
        },
        {
          label: '附件',
          prop: 'attachmentGroupId',
          slotName: 'attachmentGroupId'
        },
        { label: '委托方', prop: 'purchaseCompanyName' },
        { label: '负责人', prop: 'purchaseLink' },
        { label: '联系方式', prop: 'linkTelephone' }
      ],
      // 招标项目信息
      bidInfo: {},
      bidInfoOption: [],
      // 项目选择弹窗显示
      projectDialogVisible: false,

      // 项目成交信息
      winnerInfo: {}
    }
  },

  watch: {
    orderId: {
      handler(val) {
        if (val) this.getProjectInfo(val)
      },
      immediate: true
    }
  },

  created() {
    this.getOptionList()
  },

  methods: {
    /**
     * 获取页面配置列表
     */
    getOptionList() {
      this.agencyInfoOption = this.agencyInfo.projectId
        ? [
          { label: '项目名称', prop: 'projectName', slotName: 'projectName' },
          { label: '委托方', prop: 'purchaseCompanyName' },
          {
            label: '负责人',
            prop: '',
            slotName: 'purchaseLink',
            filter: value => `${value?.purchaseLink} ${value?.linkTelephone}`
          }
        ]
        : [{ label: '项目名称', prop: 'projectName', slotName: 'projectName' }]

      this.bidInfoOption = [
        this.showAgentInfo
          ? null
          : { label: '项目名称', prop: 'projectName', elAttrs: { span: 3 } },
        { label: '招标名称', prop: 'orderName' },
        {
          label: '招标估算',
          prop: 'budget',
          slotName: 'budget'
        },
        {
          label: '中标方式',
          prop: 'winType',
          filter: value => bidWinWayDict.translate(value)?.label
        },
        {
          label: '报价方式',
          prop: 'offerType',
          filter: value => offerTypeDict.translate(value)?.label
        },
        {
          label: '报名时间',
          prop: '',
          filter: value => {
            return `${value.signUpStartTime} - ${value.signUpEndTime}`
          }
        },
        {
          label: '开标时间',
          prop: 'openTime'
        },
        {
          label: '项目类型',
          prop: 'biddingType',
          filter: value => projectTypeDict.translate(value).label
        },
        {
          label: '发布媒体',
          prop: 'noticeMedium',
          filter: value => {
            const temp = (value || '').split(',')
            return (
              temp
                .map(mediumValue => {
                  return releaseMediaDict.translate(mediumValue)?.label
                })
                .filter(value => value)
                .join('、') || '-'
            )
          }
        },
        {
          label: '招标地点',
          filter: value => {
            if (value?.country === '中国') {
              return `${value?.province} - ${value?.city} - ${value?.area}`
            } else {
              return `${value?.country} - ${value?.foreignAddress}`
            }
          }
        },
        {
          label: '详细地址',
          prop: 'detailAddress'
        }
      ].filter(value => value)
    },

    async getProjectInfo(orderId) {
      try {
        const _results = await Promise.allSettled([
          getEntrustInfoByOrderIdApi(orderId),
          fetchInviteBidDetailApi({ orderId }),
          getWinBidLatestApi(orderId)
        ])
        // 获取委托信息
        if (_results[0].status === 'fulfilled') {
          this.agencyInfo = _results[0].value?.data || {}
        }
        // 获取招标信息
        if (_results[1].status === 'fulfilled') {
          this.bidInfo = _results[1].value?.data || {}
          this.bidInfo.projectName = this.agencyInfo.projectName
        }
        // 获取最新的成交信息
        if (_results[2].status === 'fulfilled') {
          this.winnerInfo = _results[2].value?.data || {}
        }
        this.getOptionList()
        this.$emit('selectBid', this.agencyInfo, this.bidInfo, this.winnerInfo)
      } catch (err) {
        if (err?.message) this.$message.error(err.message)
      }
    },

    async handleBidSelect(row) {
      try {
        await this.getProjectInfo(row.orderId)
      } catch (err) {
        if (err?.message) this.$message.error(err.message)
      }
    }
  }
}
</script>

<style scoped></style>
